import Tiptap from "@/components/tiptap/TipTap";
import { resolveToolbarPreset } from "@/components/tiptap/toolbar-preset";
import { FormControl, FormField, FormItem } from "@/components/ui/form";
import { Separator } from "@/components/ui/separator";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import { FormFieldProps } from "./types";

/**
 * Checks if HTML content is effectively empty (only contains empty tags or whitespace)
 * Handles cases like <p></p>, <p><br></p>, <p style="..." class="..."></p>, etc.
 */
const isEmptyHTML = (html: string | null | undefined): boolean => {
  if (!html || html.trim() === "") return true;

  // Create a temporary DOM element to parse and check the HTML
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;

  // Get text content and check if it's empty or only whitespace
  const textContent = tempDiv.textContent || tempDiv.innerText || "";
  return textContent.trim() === "";
};

/**
 * Normalizes HTML content: converts empty HTML to empty string
 */
const normalizeHTML = (html: string | null | undefined): string => {
  if (!html) return "";
  return isEmptyHTML(html) ? "" : html;
};

const RichTextField = memo(({ config, control, getValues }: FormFieldProps) => {
  const { t } = useTranslation();
  const toolbarPreset = resolveToolbarPreset(config.name ?? "");
  return (
    <div className="w-full">
      <FormField
        control={control}
        name={config.name}
        render={({ field }) => (
          <FormItem>
            <FormControl
              className={`${config.disabled ? "cursor-not-allowed" : ""}`}
            >
              <Tiptap
                {...field}
                label={t(config.label ?? "")}
                description={normalizeHTML(
                  getValues?.(field.name) ?? field.value,
                )}
                onChange={(e: any) => {
                  // Normalize empty HTML to empty string
                  const normalizedValue = normalizeHTML(e);
                  field.onChange(normalizedValue);
                }}
                disabled={config.disabled ?? false}
                toolbarPreset={toolbarPreset}
              />
            </FormControl>
          </FormItem>
        )}
      />
      {config.separator && <Separator className="my-4" />}
    </div>
  );
});

RichTextField.displayName = "RichTextField";
export default RichTextField;
